import type { ChipProps } from '@mui/material'

import { WORKFLOW_PRINTABLE_NAMES } from '@albacross/frontend-utils'
import type { DeliveryEventRecord } from '@albacross/types'
import { DELIVERY_DECISIONS, DELIVERY_EVENT_TYPES, DENIAL_REASONS } from '@albacross/types'

import { formatUtc } from '../contacts/formatUtc'

// After the contacts lookup the same key means the lookup found nothing, not a setup problem.
const DROPPED_REASONS: Record<string, string> = {
  contacts_required: 'no contacts found for this company',
}

// The raw name when this build doesn't know the key yet, so newer rows still show.
const labelOf = (labels: Record<string, string>, key: string) => labels[key] ?? key

const decisionLabel = (decision: string) => labelOf(DELIVERY_DECISIONS, decision)

const reasonLabel = (reason: string) => labelOf(DENIAL_REASONS, reason)

export const integrationName = (type: string) => labelOf(WORKFLOW_PRINTABLE_NAMES, type)

type OutcomeEvent = Pick<DeliveryEventRecord, 'type' | 'reason' | 'detail'>

const OUTCOME_COLOURS: Record<string, ChipProps['color']> = {
  handed_off: 'success',
  denied: 'error',
  dropped: 'error',
  waiting_for_contacts: 'warning',
  contacts_ready: 'warning',
  delayed_until: 'warning',
}

// Grey for segment mismatches: they're most rows and expected, so red would bury the real failures.
export const outcomeColour = ({ type, reason }: OutcomeEvent): ChipProps['color'] =>
  reason === 'segment_mismatch' ? 'default' : (OUTCOME_COLOURS[type] ?? 'default')

const handedOffTo = ({ type, detail }: OutcomeEvent) =>
  type === 'handed_off' && typeof detail?.integration === 'string' ? detail.integration : null

const eventReason = (type: string, reason: string) =>
  (type === 'dropped' && DROPPED_REASONS[reason]) || reasonLabel(reason)

export const eventText = (event: OutcomeEvent) => {
  const integration = handedOffTo(event)
  const label = integration
    ? `Passed to the ${integrationName(integration)} sender`
    : labelOf(DELIVERY_EVENT_TYPES, event.type)
  return event.reason ? `${label}: ${eventReason(event.type, event.reason)}` : label
}

const DETAILS: Record<string, { label: string; format?: (value: string) => string }> = {
  integration: { label: 'Integration', format: integrationName },
  until: { label: 'Until (UTC)', format: formatUtc },
  attempts: { label: 'Attempts' },
  contacts: { label: 'Contacts' },
  decision: { label: 'Outcome', format: decisionLabel },
  reason: { label: 'Reason', format: reasonLabel },
}

export const detailLines = (event: DeliveryEventRecord) =>
  Object.entries(event.detail ?? {})
    .filter(([key, value]) => value != null && !(key === 'integration' && handedOffTo(event)))
    .map(([key, value]) => {
      const known = DETAILS[key]
      if (!known) return `${key}: ${JSON.stringify(value)}`
      return `${known.label}: ${known.format ? known.format(String(value)) : String(value)}`
    })
